import React, { useState } from 'react';

import { DateTime } from 'luxon';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import {
  AddressFragment,
  CouponDurationTypeEnum,
  DoorToDoor__BundleEnum,
  RateGroupKindEnum,
} from '@graphql/platform';

import {
  challengerPromoMonthlySubtext,
  challengerPromoPickupSubtext,
  challengerPromoSummaryLabel,
  couponDiscountAmount,
  isAppointmentCoupon,
  isStorageCoupon,
} from '@root/components/checkout/cart/challenger_promo';
import type { ReservationSummaryProps } from '@root/components/checkout/cart/reservation_summary_props';
import { useStorageCheckoutContext } from '@root/components/checkout/context';
import type { StorageCheckoutData } from '@root/components/checkout/data';
import { StorageCheckoutStep } from '@root/components/checkout/types';
import { PLANS } from '@root/constants/pricing';
import { Commitment } from '@root/resources/types/commitment';
import { phone as phoneValidator } from '@root/resources/validator';
import { EventSchema } from '@root/resources/wt/types';

import { FROM_ISO_OPTIONS } from '@shared/calendar/calendar_utils';
import { Address } from '@shared/formatters/address';

import { formatCurrency } from '@utils/formatters';
import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { useBreakpoints } from '@utils/hooks/use_breakpoints';

import { DisposalSummaryRow } from './disposal_summary_row';
import { useChallengerPackingAmount } from './hooks/use_challenger_packing_amount';
import { MobileConfirmSticky } from './mobile_confirm_sticky';
import { ContentColumn, MainRow, Page, SummaryColumn } from './shared';
import { CHALLENGER as T } from './tokens';

const COMMITMENT_LABEL: Record<Commitment, string> = {
  [RateGroupKindEnum.Mover]: 'Month-to-month plan',
  [RateGroupKindEnum.Flexer]: '4-month minimum plan',
  [RateGroupKindEnum.Saver]: '8-month minimum plan',
};

const DISPOSAL_FROM_AMOUNT = 50;

const MOBILE = '@media (max-width: 839px)';
const DESKTOP = '@media (min-width: 840px)';

const PageTitle = styled.h1`
  margin: 0;
  font-weight: ${FontWeight.Medium};
  font-family: ${T.font.sans};
  /* Figma Heading/H3 — Medium 20/24 text-primary */
  color: ${T.color.textPrimary};
  font-size: 20px;
  line-height: 24px;

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
    color: ${T.color.textSecondary};
  }
`;

/** Figma left column: gap 12 between title-block / promo / payment-block. */
const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${T.space[12]};
`;

/** Title + cancel banner + account (Figma gap 24). */
const TopBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[24]};
  width: 100%;
`;

/** Payment card + CTA (Figma gap 16). */
const PaymentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
`;

const ConfirmMainRow = styled(MainRow)`
  ${MOBILE} {
    /* Confirm form CTA sits in-page; sticky details bar is shorter (~88px). */
    padding-bottom: 120px;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[24]};
  background: ${T.color.bgPrimary};
  border-radius: ${T.radius.md};
  width: 100%;
  box-sizing: border-box;
  padding: ${T.space[24]} ${T.space[24]} ${T.space[16]};
`;

const PromoCard = styled.div`
  background: ${T.color.bgPrimary};
  border-radius: ${T.radius.md};
  width: 100%;
  box-sizing: border-box;
  padding: 20px ${T.space[24]};
`;

const PaymentCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[24]};
  background: ${T.color.bgPrimary};
  border-radius: ${T.radius.md};
  width: 100%;
  box-sizing: border-box;
  padding: ${T.space[24]};
`;

const CardTitle = styled.h2`
  margin: 0;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Medium};
  font-size: 16px;
  line-height: 24px;
  color: ${T.color.textPrimary};
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  width: 100%;
`;

/** Figma filled account field: 74px slot, 54px input, floating 12/16 label. */
const FloatField = styled.div`
  position: relative;
  width: 100%;
  height: 74px;
`;

const FloatShell = styled.div<{ $focused?: boolean; $error?: boolean }>`
  position: relative;
  height: 54px;
  border: 1px solid
    ${({ $error, $focused }) =>
      $error
        ? '#c2410c'
        : $focused
          ? T.color.borderBrand
          : T.color.borderDefault};
  border-radius: ${T.radius.sm};
  background: ${T.color.bgPrimary};
  box-sizing: border-box;
`;

const FloatLabel = styled.span<{ $focused?: boolean; $raised?: boolean }>`
  position: absolute;
  left: 11px;
  top: -9px;
  z-index: 1;
  padding: 0 4px;
  background: ${T.color.bgPrimary};
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${({ $focused, $raised }) =>
    $focused
      ? T.color.borderBrand
      : $raised
        ? T.color.textSecondary
        : T.color.textTertiary};
  pointer-events: none;
  opacity: ${({ $raised }) => ($raised ? 1 : 0)};
  transition: opacity 0.12s ease;
`;

const FloatInput = styled.input`
  appearance: none;
  width: 100%;
  height: 54px;
  padding: 0 ${T.space[16]};
  border: none;
  border-radius: ${T.radius.sm};
  background: transparent;
  box-sizing: border-box;
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Regular};
  line-height: 24px;
  color: ${T.color.textPrimary};

  &::placeholder {
    color: ${T.color.textTertiary};
  }

  &:focus {
    outline: none;
  }
`;

const ConfirmActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
`;

/** Figma 29:3996 desktop / 144:2572 mobile — charge + cancel footer under CTA. */
const PolicyFooter = styled.p`
  margin: 0;
  text-align: center;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textTertiary};
  box-sizing: border-box;
  width: 100%;

  ${DESKTOP} {
    /* Figma 29:3998 — inset so the two designed lines wrap as on canvas. */
    padding: 0 80px;
  }

  ${MOBILE} {
    font-size: 10px;
    font-weight: ${FontWeight.Regular};
    line-height: 15px;
    color: ${T.color.textSecondary};
    word-break: break-word;
  }
`;

/** Figma 29:3996 / 29:3843 — desktop-only hard breaks; hidden on mobile. */
const DesktopOnlyBreak = styled.br`
  ${MOBILE} {
    display: none;
  }
`;

const PolicyFooterLink = styled.button`
  appearance: none;
  display: inline;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${T.color.linkBrand};
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;

  ${MOBILE} {
    font-weight: ${FontWeight.Medium};
  }
`;

const ConfirmButton = styled.button<{ disabled?: boolean }>`
  appearance: none;
  width: 100%;
  height: 48px;
  border: none;
  border-radius: ${T.radius.md};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled }) =>
    disabled ? T.color.bgButtonDisabled : T.color.textBrand};
  color: ${({ disabled }) =>
    disabled ? T.color.textDisabled : T.color.textInverse};
  font-family: ${T.font.sans};
  /* Figma 29:3842 — Body/Large 600 Medium 16/24 */
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.92)};
  }
`;

/** Figma 29:3843 — Body/Small 600; Terms of Use Demi Bold. */
const TermsCopy = styled.p`
  margin: 0;
  text-align: center;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textTertiary};
  width: 100%;
  word-break: break-word;

  a {
    color: inherit;
    font-weight: ${FontWeight.Semibold};
    text-decoration: none;
  }

  ${MOBILE} {
    /* Figma 144:2624 — same Body/Small 400 Mobile as the policy footer above. */
    font-size: 10px;
    font-weight: ${FontWeight.Regular};
    line-height: 15px;
    color: ${T.color.textSecondary};

    a {
      font-weight: ${FontWeight.Medium};
    }
  }
`;

type FloatAccountFieldProps = {
  id?: string;
  label: string;
  value: string;
  type?: string;
  autoComplete?: string;
  error?: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
};

const FloatAccountField: React.FC<FloatAccountFieldProps> = ({
  id,
  label,
  value,
  type = 'text',
  autoComplete,
  error,
  disabled,
  onChange,
  onBlur,
}) => {
  const [focused, setFocused] = useState(false);
  const raised = focused || value.trim().length > 0;

  return (
    <FloatField id={id}>
      <FloatShell $focused={focused} $error={error}>
        <FloatLabel $focused={focused} $raised={raised}>
          {label}
        </FloatLabel>
        <FloatInput
          type={type}
          value={value}
          autoComplete={autoComplete}
          placeholder={raised ? '' : label}
          aria-label={label}
          aria-invalid={error || undefined}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          onChange={(e) => onChange(e.target.value)}
        />
      </FloatShell>
    </FloatField>
  );
};

const SideStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: ${T.layout.summaryWidth};
`;

const SideCard = styled.div`
  background: ${T.color.summarySurface};
  border-radius: ${T.radius.md};
  padding: 20px ${T.space[16]} ${T.space[24]};
  width: 100%;
  box-sizing: border-box;
`;

const BookingSideCard = styled(SideCard)`
  padding: ${T.space[24]} ${T.space[16]} 19px;
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
`;

const SideHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 9px;
  width: 100%;
`;

const SideTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;
  color: ${T.color.textSecondary};
`;

const ChangeButton = styled.button`
  appearance: none;
  cursor: pointer;
  flex-shrink: 0;
  padding: ${T.space[8]} ${T.space[12]};
  border: 1px solid ${T.color.borderDefault};
  border-radius: ${T.radius.sm};
  background: transparent;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 14px;
  color: ${T.color.textPrimary};
`;

const DetailBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const DetailLabel = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  color: ${T.color.textSecondary};
`;

const DetailValue = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textPrimary};
`;

const ArrivalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  width: 100%;
  padding: 0 4px;
`;

const BookingHeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  width: 100%;
  padding: 0 0 20px;
  border-bottom: 1px solid ${T.color.borderDefault};
`;

const PlanLabel = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${T.color.textPrimary};
`;

const CostBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: ${T.space[16]};
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  color: ${T.color.textSecondary};
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${T.space[8]};
  font-size: 14px;
  line-height: 20px;
  color: ${T.color.textSecondary};
`;

/** Figma promo discount row — brand teal. */
const PromoLine = styled(Line)`
  color: ${T.color.textBrandDark};
  padding: 2px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: flex-end;
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  color: ${T.color.textSecondary};
`;

const Timeline = styled.div`
  background: ${T.color.timelineSurface};
  border: 1px solid rgba(198, 170, 119, 0.4);
  border-radius: ${T.radius.md};
  padding: ${T.space[16]};
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
`;

const TimelineRow = styled.div`
  display: flex;
  gap: ${T.space[16]};
  align-items: flex-start;
`;

/**
 * Figma 707:5568 — 8px gold dots with 1px connectors (rgba brand-secondary 40%)
 * bridging each row gap down to the next dot center.
 */
const BulletCol = styled.div<{ $connect?: boolean }>`
  position: relative;
  width: 8px;
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 8px;

  ${({ $connect }) =>
    $connect
      ? `
    &::after {
      content: '';
      position: absolute;
      top: 12px;
      bottom: calc(-${T.space[16]} - 12px);
      left: 50%;
      width: 1px;
      margin-left: -0.5px;
      border-radius: 999px;
      background: rgba(198, 170, 119, 0.4);
    }
  `
      : ''}
`;

const Bullet = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${T.color.iconBrandSecondary};
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

const TimelineCopy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TimelineText = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 25px;
  color: ${T.color.textSecondary};
`;

const TimelineSubtext = styled.div`
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textSecondary};
`;

const TimelineAmount = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 25px;
  color: ${T.color.textSecondary};
  white-space: nowrap;
`;

const ErrorBox = styled.div`
  margin-top: ${T.space[8]};
  text-align: center;
  color: #c2410c;
  font-size: 14px;
`;

export type ChallengerConfirmReservationProps = {
  values: StorageCheckoutData;
  reservationSummary: ReservationSummaryProps;
  cancelBy: DateTime;
  bookingError: React.ReactNode;
  buttonDisabled: boolean;
  buttonLoading: boolean;
  onConfirm: () => void | Promise<void>;
  onEdit: (step: StorageCheckoutStep) => void;
  onCancellationLinkClick: () => void;
  onChangeName: (value: string) => void;
  onChangeEmail: (value: string) => void;
  onChangePhone: (value: string) => void;
  phoneError?: string;
  promoCodeElement: React.ReactNode;
  paymentMethodElement: React.ReactNode;
};

/** Figma: `7AM - 10AM` (no minutes when :00, no space before AM/PM). */
const formatConfirmClock = (value: DateTime): string => {
  const meridiem = value.toFormat('a').replace(/\s/g, '').toUpperCase();
  if (value.minute === 0) {
    return `${value.toFormat('h')}${meridiem}`;
  }
  return `${value.toFormat('h:mm')}${meridiem}`;
};

export const ChallengerConfirmReservation: React.FC<
  ChallengerConfirmReservationProps
> = ({
  values,
  reservationSummary,
  cancelBy,
  bookingError,
  buttonDisabled,
  buttonLoading,
  onConfirm,
  onEdit,
  onCancellationLinkClick,
  onChangeName,
  onChangeEmail,
  onChangePhone,
  phoneError,
  promoCodeElement,
  paymentMethodElement,
}) => {
  const { isDesktop } = useBreakpoints();
  const trackFunnelEvent = useTrackFunnelEvents();
  const { pricingSummary } = useStorageCheckoutContext();
  const {
    planSize,
    commitment,
    bundleKind,
    disposalSelected,
    dateScheduled,
    coupon,
    customerToken,
  } = values;
  const accountDetailsLocked = !!customerToken;
  // Mobile filled confirm (Figma 707:15644) uses “Reserve storage”; desktop keeps “Confirm reservation”.
  const actionLabel = isDesktop ? 'Confirm reservation' : 'Reserve storage';

  const planText =
    planSize && PLANS[planSize]
      ? `${PLANS[planSize].label} - ${PLANS[planSize].description}`
      : 'Storage plan';

  const tax =
    (reservationSummary.storagePlanTax || 0) +
    (reservationSummary.protectionPlanTax || 0);
  const fullMonthlyTotal = reservationSummary.summaryLoaded
    ? reservationSummary.monthlyTotalAmount + tax
    : 0;
  const monthlyBaseAmount = reservationSummary.summaryLoaded
    ? reservationSummary.storagePlanAmount +
      reservationSummary.protectionPlanAmount
    : 0;

  const { amount: packingQuoteAmount, loading: packingQuoteLoading } =
    useChallengerPackingAmount();
  const packingAmount =
    bundleKind === DoorToDoor__BundleEnum.PickupAndPacking &&
    !packingQuoteLoading
      ? (packingQuoteAmount ?? 0)
      : 0;
  // Match packing section copy (API bundle name is often "Premium").
  const packingLabel =
    bundleKind === DoorToDoor__BundleEnum.PickupAndPacking
      ? 'Full-service packing'
      : null;

  const scheduledArrivalAmount = dateScheduled?.feeAmount?.value ?? 0;
  const gasFeeAmount = pricingSummary?.gasFeeAmount ?? 0;
  const disposalAmount = disposalSelected ? DISPOSAL_FROM_AMOUNT : 0;
  const pickupFeesBeforePromo =
    packingAmount + scheduledArrivalAmount + gasFeeAmount + disposalAmount;

  // Figma 913:43587 — storage promos under Storage plan (before Tax);
  // appointment promos under Pick-up fees (below Gas fee).
  const storagePromoDiscount =
    coupon && isStorageCoupon(coupon) && reservationSummary.summaryLoaded
      ? couponDiscountAmount(coupon, monthlyBaseAmount)
      : 0;
  const appointmentPromoDiscount =
    coupon && isAppointmentCoupon(coupon)
      ? couponDiscountAmount(coupon, pickupFeesBeforePromo)
      : 0;

  const storageSectionTotal = Math.max(
    0,
    fullMonthlyTotal - storagePromoDiscount,
  );
  const ongoingMonthlyTotal =
    coupon && isStorageCoupon(coupon)
      ? coupon.duration === CouponDurationTypeEnum.Once
        ? fullMonthlyTotal
        : storageSectionTotal
      : fullMonthlyTotal;
  const pickupTotal = Math.max(
    0,
    pickupFeesBeforePromo - appointmentPromoDiscount,
  );
  const dueOnPickup = storageSectionTotal + pickupTotal;

  const appointment = values.datePreferred;
  const windowLabel =
    dateScheduled?.fromTime && dateScheduled?.tillTime
      ? `${formatConfirmClock(
          DateTime.fromISO(dateScheduled.fromTime, FROM_ISO_OPTIONS),
        )} - ${formatConfirmClock(
          DateTime.fromISO(dateScheduled.tillTime, FROM_ISO_OPTIONS),
        )}`
      : undefined;
  const appointmentLabel = appointment
    ? `${appointment.toFormat('cccc, MMM d, yyyy')}${
        windowLabel ? ` between ${windowLabel}` : ''
      }`
    : 'Select a pickup date';

  const pickupDayLabel = appointment
    ? appointment.toFormat('MMMM d, yyyy')
    : undefined;
  const monthStartLabel = appointment
    ? `Starting ${appointment.plus({ months: 1 }).toFormat('MMMM d, yyyy')}`
    : undefined;
  // Appointment promos annotate pick-up day; storage promos use monthly subtext.
  const pickupSubtext =
    coupon && isAppointmentCoupon(coupon)
      ? challengerPromoPickupSubtext(pickupDayLabel)
      : pickupDayLabel;
  const monthlySubtext = coupon
    ? challengerPromoMonthlySubtext(coupon, fullMonthlyTotal, monthStartLabel)
    : monthStartLabel;

  const showPickupFees =
    packingAmount > 0 ||
    scheduledArrivalAmount > 0 ||
    gasFeeAmount > 0 ||
    disposalSelected ||
    appointmentPromoDiscount > 0;

  return (
    <Page>
      <ConfirmMainRow>
        <ContentColumn>
          <FormStack>
            <TopBlock>
              <PageTitle>Confirm reservation</PageTitle>

              <Card>
                <CardTitle>Account details</CardTitle>
                <FieldStack>
                  <FloatAccountField
                    label="Full name"
                    value={values.name ?? ''}
                    onChange={onChangeName}
                    autoComplete="name"
                    disabled={accountDetailsLocked}
                  />
                  <FloatAccountField
                    label="Email"
                    type="email"
                    value={values.email ?? ''}
                    onChange={onChangeEmail}
                    autoComplete="email"
                    disabled={accountDetailsLocked}
                  />
                  <div>
                    <FloatAccountField
                      id="phone-section"
                      label="Mobile number"
                      type="tel"
                      value={values.phone ?? ''}
                      onChange={onChangePhone}
                      autoComplete="tel"
                      error={!!phoneError}
                      onBlur={() => {
                        // Match classic PhoneInput: complete phone capture on blur.
                        const phone = values.phone ?? '';
                        if (phoneValidator(phone)) {
                          trackFunnelEvent({
                            schema: EventSchema.WWW__PhoneCaptureCompleted,
                            action: 'submit',
                            metadata: { phone_number: phone },
                          });
                        }
                      }}
                    />
                    {phoneError && <ErrorBox>{phoneError}</ErrorBox>}
                  </div>
                </FieldStack>
              </Card>
            </TopBlock>

            <PromoCard>{promoCodeElement}</PromoCard>

            <PaymentBlock>
              <PaymentCard>
                <CardTitle>Credit card details</CardTitle>
                {paymentMethodElement}
              </PaymentCard>

              <ConfirmActions>
                <ConfirmButton
                  type="button"
                  disabled={buttonDisabled || buttonLoading}
                  onClick={onConfirm}
                >
                  {buttonLoading ? 'Reserving…' : actionLabel}
                </ConfirmButton>
                {bookingError}
                <PolicyFooter>
                  You will be charged upon completion of your pickup. You have
                  until {cancelBy.toFormat('MMMM d')} to cancel.{' '}
                  <DesktopOnlyBreak />
                  See Clutter&apos;s{' '}
                  <PolicyFooterLink
                    type="button"
                    onClick={onCancellationLinkClick}
                  >
                    cancellation and booking policy
                  </PolicyFooterLink>{' '}
                  for more details.
                </PolicyFooter>
                <TermsCopy>
                  By clicking &quot;{actionLabel},&quot; you agree to
                  Clutter&apos;s{' '}
                  <a href="/legal/tos" target="_blank" rel="noreferrer">
                    Terms of Use
                  </a>{' '}
                  and to receive transactional <DesktopOnlyBreak />
                  SMS messages about your appointment or account. Message and
                  data rates may apply.
                </TermsCopy>
              </ConfirmActions>
            </PaymentBlock>
          </FormStack>
        </ContentColumn>

        <SummaryColumn>
          <SideStack>
            <SideCard>
              <ArrivalBody>
                <SideHeader>
                  <SideTitle>Arrival details</SideTitle>
                  <ChangeButton
                    type="button"
                    onClick={() =>
                      onEdit(StorageCheckoutStep.AddressAndLeadInfo)
                    }
                  >
                    Change
                  </ChangeButton>
                </SideHeader>
                <DetailBlock>
                  <DetailLabel>Address</DetailLabel>
                  <DetailValue>
                    <Address
                      inline
                      address={(values.address as AddressFragment) ?? {}}
                    />
                  </DetailValue>
                </DetailBlock>
                <DetailBlock>
                  <DetailLabel>Date</DetailLabel>
                  <DetailValue>{appointmentLabel}</DetailValue>
                </DetailBlock>
              </ArrivalBody>
            </SideCard>

            <BookingSideCard>
              <BookingHeaderBlock>
                <SideHeader>
                  <SideTitle>Booking summary</SideTitle>
                  <ChangeButton
                    type="button"
                    onClick={() => onEdit(StorageCheckoutStep.PlanSize)}
                  >
                    Change
                  </ChangeButton>
                </SideHeader>
                <PlanLabel>{planText}</PlanLabel>
              </BookingHeaderBlock>

              <CostBlock>
                <SectionLabel>Storage plan / month</SectionLabel>
                {commitment && (
                  <Line>
                    <span>{COMMITMENT_LABEL[commitment]}</span>
                    <span>
                      {reservationSummary.summaryLoaded
                        ? formatCurrency(reservationSummary.storagePlanAmount)
                        : '—'}
                    </span>
                  </Line>
                )}
                {reservationSummary.protectionPlanAmount > 0 && (
                  <Line>
                    <span>
                      {reservationSummary.protectionPlanLabel} protection
                    </span>
                    <span>
                      {formatCurrency(reservationSummary.protectionPlanAmount)}
                    </span>
                  </Line>
                )}
                {coupon && storagePromoDiscount > 0 && (
                  <PromoLine>
                    <span>{challengerPromoSummaryLabel(coupon)}</span>
                    <span>-{formatCurrency(storagePromoDiscount)}</span>
                  </PromoLine>
                )}
                <Line>
                  <span>Tax</span>
                  <span>
                    {reservationSummary.summaryLoaded
                      ? formatCurrency(tax)
                      : '—'}
                  </span>
                </Line>
                <TotalRow>
                  {reservationSummary.summaryLoaded
                    ? formatCurrency(storageSectionTotal)
                    : '—'}
                </TotalRow>
              </CostBlock>

              {showPickupFees && (
                <CostBlock>
                  <SectionLabel>Pick-up fees</SectionLabel>
                  {packingLabel && packingAmount > 0 && (
                    <Line>
                      <span>{packingLabel}</span>
                      <span>{formatCurrency(packingAmount)}</span>
                    </Line>
                  )}
                  {scheduledArrivalAmount > 0 && (
                    <Line>
                      <span>Scheduled arrival</span>
                      <span>{formatCurrency(scheduledArrivalAmount)}</span>
                    </Line>
                  )}
                  {gasFeeAmount > 0 && (
                    <Line>
                      <span>Gas fee</span>
                      <span>{formatCurrency(gasFeeAmount)}</span>
                    </Line>
                  )}
                  {coupon && appointmentPromoDiscount > 0 && (
                    <PromoLine>
                      <span>{challengerPromoSummaryLabel(coupon)}</span>
                      <span>-{formatCurrency(appointmentPromoDiscount)}</span>
                    </PromoLine>
                  )}
                  {disposalSelected && (
                    <DisposalSummaryRow amount={disposalAmount} />
                  )}
                  <TotalRow>{formatCurrency(pickupTotal)}</TotalRow>
                </CostBlock>
              )}

              <Timeline>
                <TimelineRow>
                  <BulletCol $connect aria-hidden>
                    <Bullet />
                  </BulletCol>
                  <TimelineCopy>
                    <TimelineText>Total due now</TimelineText>
                  </TimelineCopy>
                  <TimelineAmount>$0.00</TimelineAmount>
                </TimelineRow>
                <TimelineRow>
                  <BulletCol $connect aria-hidden>
                    <Bullet />
                  </BulletCol>
                  <TimelineCopy>
                    <TimelineText>Total due on pick-up day</TimelineText>
                    {pickupSubtext && (
                      <TimelineSubtext>{pickupSubtext}</TimelineSubtext>
                    )}
                  </TimelineCopy>
                  <TimelineAmount>
                    {reservationSummary.summaryLoaded
                      ? formatCurrency(dueOnPickup)
                      : '—'}
                  </TimelineAmount>
                </TimelineRow>
                <TimelineRow>
                  <BulletCol aria-hidden>
                    <Bullet />
                  </BulletCol>
                  <TimelineCopy>
                    <TimelineText>Total due each month</TimelineText>
                    {monthlySubtext && (
                      <TimelineSubtext>{monthlySubtext}</TimelineSubtext>
                    )}
                  </TimelineCopy>
                  <TimelineAmount>
                    {reservationSummary.summaryLoaded
                      ? formatCurrency(ongoingMonthlyTotal)
                      : '—'}
                  </TimelineAmount>
                </TimelineRow>
              </Timeline>
            </BookingSideCard>
          </SideStack>
        </SummaryColumn>
      </ConfirmMainRow>
      {/* Figma 707:15644 / 707:15485 — mobile price + reservation details sheet. */}
      {reservationSummary.summaryLoaded ? (
        <MobileConfirmSticky onEdit={onEdit} />
      ) : null}
    </Page>
  );
};
